/**
 * The album: a dog's real photos and clips, over the top of the 3D scene.
 * Content comes from public/album/<dog>/index.json; drop files in, list them.
 */
interface AlbumItem {
  /** live = a Live Photo's few seconds: plays silently on a loop, no controls */
  type: 'photo' | 'video' | 'live';
  src: string;
  poster?: string;
  caption?: string;
  /** src is a data URL taken in the app, not a file under album/ */
  local?: boolean;
  /** src is a photo shared on the license (served by the API) */
  remote?: boolean;
  device?: string;
  at?: number;
}
interface AlbumIndex {
  title: string;
  items: AlbumItem[];
}

export class Album {
  private overlay: HTMLElement;
  private grid: HTMLElement;
  private viewer: HTMLElement;
  private loaded = false;
  private items: AlbumItem[] = [];

  constructor(
    private dogId: string,
    private dogName: string,
  ) {
    this.overlay = document.querySelector<HTMLElement>('#album')!;
    this.grid = this.overlay.querySelector<HTMLElement>('.album-grid')!;
    this.viewer = this.overlay.querySelector<HTMLElement>('.album-viewer')!;
    this.overlay.querySelector<HTMLElement>('.album-title')!.textContent = dogName;
    this.overlay.querySelector<HTMLButtonElement>('.album-close')!.addEventListener('click', () => this.close());
    this.viewer.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).tagName !== 'VIDEO') this.closeViewer();
    });
  }

  get isOpen(): boolean {
    return !this.overlay.hidden;
  }

  async open(): Promise<void> {
    this.overlay.hidden = false;
    if (!this.loaded) await this.load();
  }

  close(): void {
    this.closeViewer();
    this.overlay.hidden = true;
  }

  /** photos taken in the app (data URLs), shown first */
  localPhotos: () => { dataUrl: string; caption: string; at: number }[] = () => [];
  /** delete one of those (they're only on this phone) */
  onDeleteLocal?: (at: number) => void;
  /** photos everyone on the license has taken (from the API; empty offline or without a code) */
  remotePhotos: () => Promise<{ at: number; device: string; caption: string; by: string; avatar: string; url: string }[]> = async () => [];
  /** may this phone delete a shared photo taken on `device`? (its own, or the owner's phone) */
  canDeleteRemote: (device: string) => boolean = () => false;
  onDeleteRemote?: (at: number) => Promise<boolean>;

  /** re-read on next open */
  invalidate(): void {
    this.loaded = false;
  }

  private async load(): Promise<void> {
    this.loaded = true;
    try {
      const res = await fetch(`album/${this.dogId}/index.json`);
      const idx = (await res.json()) as AlbumIndex;
      this.items = idx.items;
    } catch {
      this.items = [];
    }
    const shared = await this.remotePhotos();
    const sharedAt = new Set(shared.map((p) => p.at));
    // a photo this phone took and has already shared shows once, as the shared copy
    const mine = this.localPhotos()
      .filter((p) => !sharedAt.has(p.at))
      .map((p): AlbumItem => ({ type: 'photo', src: p.dataUrl, caption: p.caption, local: true, at: p.at }));
    const theirs = shared.map((p): AlbumItem => ({ type: 'photo', src: p.url, caption: `${p.caption} · 📷 ${p.avatar} ${p.by}`, remote: true, at: p.at, device: p.device }));
    const taken = [...mine, ...theirs].sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
    this.items = [...taken, ...this.items];
    this.grid.textContent = '';
    if (!this.items.length) {
      const p = document.createElement('p');
      p.className = 'album-empty';
      p.textContent = `No photos of ${this.dogName} yet — add some to public/album/${this.dogId}/`;
      this.grid.append(p);
      return;
    }
    this.items.forEach((item, i) => {
      const tile = document.createElement('button');
      tile.className = 'album-tile';
      tile.type = 'button';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = item.local || item.remote ? item.src : `album/${this.dogId}/${item.type === 'photo' ? item.src : (item.poster ?? item.src)}`;
      img.alt = item.caption ?? '';
      tile.append(img);
      if (item.type !== 'photo') {
        const badge = document.createElement('span');
        badge.className = 'album-badge';
        badge.textContent = item.type === 'live' ? 'LIVE' : '▶';
        tile.append(badge);
      }
      tile.addEventListener('click', () => this.show(i));
      this.grid.append(tile);
    });
  }

  private show(i: number): void {
    const item = this.items[i];
    this.viewer.textContent = '';
    const src = item.local || item.remote ? item.src : `album/${this.dogId}/${item.src}`;
    if (item.type === 'video' || item.type === 'live') {
      const v = document.createElement('video');
      v.src = src;
      v.poster = item.poster ? `album/${this.dogId}/${item.poster}` : '';
      v.autoplay = true;
      v.playsInline = true;
      if (item.type === 'live') {
        v.muted = true;
        v.loop = true;
      } else {
        v.controls = true;
      }
      this.viewer.append(v);
    } else {
      const img = document.createElement('img');
      img.src = src;
      img.alt = item.caption ?? '';
      this.viewer.append(img);
    }
    if (item.caption) {
      const c = document.createElement('p');
      c.textContent = item.caption;
      this.viewer.append(c);
    }
    if (item.local && item.at !== undefined) {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'album-delete';
      del.textContent = 'delete this photo';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!confirm('Delete this photo from this phone?')) return;
        this.onDeleteLocal?.(item.at!);
        this.closeViewer();
        this.loaded = false;
        void this.load();
      });
      this.viewer.append(del);
    }
    if (item.remote && item.at !== undefined && item.device && this.canDeleteRemote(item.device)) {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'album-delete';
      del.textContent = 'delete for everyone';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!confirm('Delete this photo for everyone on the license?')) return;
        void this.onDeleteRemote?.(item.at!).then(() => {
          this.closeViewer();
          this.loaded = false;
          void this.load();
        });
      });
      this.viewer.append(del);
    }
    this.viewer.hidden = false;
  }

  private closeViewer(): void {
    this.viewer.querySelector('video')?.pause();
    this.viewer.textContent = '';
    this.viewer.hidden = true;
  }
}
